// ThankYou.js
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "../../customHooks/reactGA";
import emailjs from "@emailjs/browser";

import iconLight from '../../assets/carp_house_plain.png';
import iconDark from '../../assets/carp-house-plain-dark.png';

import {
	Box,
	Typography,
	TextField,
	Button,
	Snackbar,
	Alert,
	MenuItem,
} from "@mui/material";

const ADS_ID = "AW-17688518709";
const ADS_CONVERSION_LABEL = ""; 

const ThankYou = (props) => {
	const { trackEvent } = useAnalytics();
	const location = useLocation();
	const navigate = useNavigate();

	// Step 1 info passed from Form submit
	const step1 = location.state || null;

	// If someone manually hits /thank-you with no state, you can redirect home
	React.useEffect(() => {
		if (!step1) navigate("/", { replace: true });
	}, [step1, navigate]);

	// Fire conversion + track “arrival” once
	React.useEffect(() => {
		if (!step1) return;

		trackEvent("Lead Form", "View", "Thank You Page");

		if (ADS_CONVERSION_LABEL) {
			window.gtag?.("event", "conversion", {
				send_to: `${ADS_ID}/${ADS_CONVERSION_LABEL}`,
			});
		}
	}, [step1, trackEvent]);

	// Step 2 fields
	const [email, setEmail] = React.useState("");
	const [bedBath, setBedBath] = React.useState("");
	const [sqft, setSqft] = React.useState("");
	const [houseAge, setHouseAge] = React.useState("");
	const [garage, setGarage] = React.useState("");
	const [basementType, setBasementType] = React.useState("");
	const [whySelling, setWhySelling] = React.useState("");
	const [asIsValue, setAsIsValue] = React.useState("");
	const [arvValue, setArvValue] = React.useState("");
	const [closeTimeline, setCloseTimeline] = React.useState("");

	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [snackbar, setSnackbar] = React.useState({
		open: false,
		severity: "success",
		message: "",
	});

	const closeSnackbar = (_, reason) => {
		if (reason === "clickaway") return;
		setSnackbar((s) => ({ ...s, open: false }));
	};

	const clearStep2 = () => {
		setEmail("");
		setBedBath("");
		setSqft("");
		setHouseAge("");
		setGarage("");
		setBasementType("");
		setWhySelling("");
		setAsIsValue("");
		setArvValue("");
		setCloseTimeline("");
	};

	const sendStep2 = async (e) => {
		e.preventDefault();
		if (isSubmitting || !step1) return;

		setIsSubmitting(true);

		try {
			await emailjs.send(
				"service_6zy89mx",
				"template_6h3q4h4",
				{
					// include Step 1 again so it ties to the original lead
					customer_name: step1.customerName,
					property_address: step1.address,
					phone: step1.phone,

					// Step 2 details
					email_address: email,
					bed_bath: bedBath,
					approximate_sqft: sqft,
					house_age: houseAge,
					has_garage: garage,
					foundation_basement: basementType,
					why_selling: whySelling,
					as_is_value: asIsValue,
					after_renovation_value: arvValue,
					close_timeline: closeTimeline,

					// helpful metadata
					source_page: window.location.pathname,
					submitted_at: new Date().toISOString(),
				},
				"9dDfLbFoZurbCagy1"
			);

			trackEvent("Lead Form", "Submit", "Step 2 Details");

			setSnackbar({
				open: true,
				severity: "success",
				message: "Thank you! Extra details sent to our team.",
			});

			clearStep2();
		} catch (err) {
			console.log(err);
			setSnackbar({
				open: true,
				severity: "error",
				message: "Sorry — something went wrong sending the details. Please try again.",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const labelColor =
		props?.theme?.palette?.mode === "dark"
			? props.theme.palette.common.white
			: props?.theme?.palette?.primary?.main || "inherit";

	if (!step1) return null;

	return (
		<div>

			<section
				className="landing"
				style={{
					backgroundColor:
						props.theme.palette.mode === "dark"
							? props.theme.palette.primary.dark
							: '#e4e4e4',
					backgroundImage:
						props.theme.palette.mode === "dark"
							? `url(${iconDark})`
							: `url(${iconLight})`
				}}
			>
				<div className="flex-container main-text-container">
					<div>
						<h1
							className="heading-title l1 heading-title--pop"
							style={{
								color:
									props.theme.palette.mode === "dark"
										? props.theme.palette.warning.main
										: props.theme.palette.primary.main,
							}}
						>
							Thanks for reaching out to CarpHomes!
						</h1>
						<p
							className="heading-title l2 heading-title--pop"
							style={{
								color:
									props.theme.palette.mode === "dark"
										? props.theme.palette.common.white
										: props.theme.palette.primary.dark,
							}}
						>
							We’ve received your info and someone from our team will reach out as soon as possible — typically within
							minutes, and always within 24 hours.
						</p>
						{/* <p
							className="heading-title l2 heading-title--pop"
							style={{
								color:
									props.theme.palette.mode === "dark"
										? props.theme.palette.common.white
										: props.theme.palette.primary.dark,
							}}
						>
							If you have a moment, sharing a few more details about your property below can help us prepare and
							potentially speed things up.
						</p> */}
					</div>
				</div>
			</section>

			<Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 6 }}>
				{/* <Typography variant="h4" gutterBottom>
					Thanks for reaching out to CarpHomes!
				</Typography>

				<Typography variant="body1" sx={{ mb: 3 }}>
					We’ve received your info and someone from our team will reach out as soon as possible — typically within
					minutes, and always within 24 hours.
				</Typography> */}

				<Typography variant="body1" sx={{ mb: 4 }}>
					If you have a moment, sharing a few more details about your property below can help us prepare and
					potentially speed things up.
				</Typography>

				<Box
					component="form"
					noValidate
					autoComplete="off"
					onSubmit={sendStep2}
					sx={{ display: "flex", flexDirection: "column", gap: 2 }}
				>
					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							Email Address
						</Typography>
						<TextField
							fullWidth
							name="email_address"
							label="Email address"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							Bed / Bath
						</Typography>
						<TextField
							fullWidth
							name="bed_bath"
							label="e.g., 3 bed / 2 bath"
							value={bedBath}
							onChange={(e) => setBedBath(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							Approximate Sq Ft
						</Typography>
						<TextField
							fullWidth
							name="approximate_sqft"
							label="e.g., 1,850"
							value={sqft}
							onChange={(e) => setSqft(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							How old is your house?
						</Typography>
						<TextField
							fullWidth
							name="house_age"
							label="e.g., built in 1978"
							value={houseAge}
							onChange={(e) => setHouseAge(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							Do you have a garage?
						</Typography>
						<TextField
							fullWidth
							select
							name="has_garage"
							label="Select one"
							value={garage}
							onChange={(e) => setGarage(e.target.value)}
						>
							<MenuItem value="">Select</MenuItem>
							<MenuItem value="Yes">Yes</MenuItem>
							<MenuItem value="No">No</MenuItem>
						</TextField>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							Basement / Crawl Space / Slab Foundation?
						</Typography>
						<TextField
							fullWidth
							name="foundation_basement"
							label="e.g., finished basement, crawl space, slab"
							value={basementType}
							onChange={(e) => setBasementType(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							Why are you selling?
						</Typography>
						<TextField
							fullWidth
							multiline
							minRows={3}
							name="why_selling"
							label="Short summary"
							value={whySelling}
							onChange={(e) => setWhySelling(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							What do you think it’s worth as-is?
						</Typography>
						<TextField
							fullWidth
							name="as_is_value"
							label="e.g., $175,000"
							value={asIsValue}
							onChange={(e) => setAsIsValue(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							What do you think it’s worth after renovation?
						</Typography>
						<TextField
							fullWidth
							name="after_renovation_value"
							label="e.g., $245,000"
							value={arvValue}
							onChange={(e) => setArvValue(e.target.value)}
						/>
					</Box>

					<Box>
						<Typography mb="0.25em" color={labelColor} fontSize="1rem">
							When would you like to close?
						</Typography>
						<TextField
							fullWidth
							name="close_timeline"
							label="e.g., ASAP / 30 days / 60-90 days"
							value={closeTimeline}
							onChange={(e) => setCloseTimeline(e.target.value)}
						/>
					</Box>

					<Box sx={{ mt: 2 }}>
						<Button type="submit" variant="contained" color="warning" disabled={isSubmitting}>
							{isSubmitting ? "Sending..." : "Send Extra Details"}
						</Button>
					</Box>
				</Box>

				<Snackbar
					open={snackbar.open}
					autoHideDuration={6000}
					onClose={closeSnackbar}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
						{snackbar.message}
					</Alert>
				</Snackbar>
			</Box>
			<Box textAlign={'center'}>
				<Button
					variant="text"
					color="primary"
					onClick={() => navigate("/")}
				>
					← Back to Homepage
				</Button>
			</Box>
		</div>
	);
};

export default ThankYou;